import { useEffect } from 'react';

interface PageSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  imageUrl?: string;
}

export const usePageSEO = ({ title, description, keywords, canonicalUrl, imageUrl }: PageSEOProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', description);
    
    // Update or create meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords);
    }
    
    // Update or create canonical URL
    if (canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonicalUrl);
    }
    
    // Update or create Open Graph meta tags
    const updateOrCreateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };
    
    // Set Open Graph tags
    updateOrCreateMetaTag('og:title', title);
    updateOrCreateMetaTag('og:description', description);
    if (canonicalUrl) {
      updateOrCreateMetaTag('og:url', canonicalUrl);
    }
    if (imageUrl) {
      updateOrCreateMetaTag('og:image', imageUrl);
      updateOrCreateMetaTag('og:image:width', '1200');
      updateOrCreateMetaTag('og:image:height', '630');
      updateOrCreateMetaTag('og:image:type', 'image/svg+xml');
    }
    updateOrCreateMetaTag('og:type', 'website');
    
    // Cleanup function to restore original title if needed
    return () => {
      // You could restore a default title here if needed
    };
  }, [title, description, keywords, canonicalUrl, imageUrl]);
};
